import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';
import { SkeletonCard } from '../components/common/Skeleton';
import MemberCardModal from '../components/common/MemberCardModal';
import useChurchSettings from '../hooks/useChurchSettings';
import usePermissions from '../hooks/usePermissions';

const STATUS_LABELS  = { nouveau:'Nouveau', membre:'Membre régulier', serviteur:'Serviteur', invite:'Invité' };
const STATUS_COLORS  = {
  nouveau:   { bg:'#faf5ff', color:'#7c3aed', border:'#ddd6fe' },
  membre:    { bg:'#f0fdf4', color:'#16a34a', border:'#bbf7d0' },
  serviteur: { bg:'#eff6ff', color:'#1d4ed8', border:'#bfdbfe' },
  invite:    { bg:'#fff7ed', color:'#c2410c', border:'#fed7aa' },
};
const GENDER_LABELS  = { homme:'Homme', femme:'Femme', enfant:'Enfant' };
const MARITAL_LABELS = { celibataire:'Célibataire', marie:'Marié(e)', divorce:'Divorcé(e)', veuf:'Veuf / Veuve' };

/* ── Graphique barres SVG ── */
function BarChart({ data }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.v), 1);
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:'6px', height:'100px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'4px', justifyContent:'flex-end', height:'100%' }}>
          <span style={{ fontSize:'0.68rem', fontWeight:700, color:'var(--primary)' }}>{d.v}</span>
          <div style={{ width:'100%', background:'var(--primary)', borderRadius:'3px 3px 0 0', height:`${(d.v/max)*76}px`, minHeight:4, opacity:0.85 }} />
          <span style={{ fontSize:'0.62rem', color:'var(--text-secondary)', whiteSpace:'nowrap' }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Ligne d'info ── */
function InfoRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display:'flex', gap:'12px', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
      <div style={{ width:'170px', flexShrink:0, fontSize:'0.8rem', color:'var(--text-secondary)', fontWeight:600 }}>{label}</div>
      <div style={{ fontSize:'0.875rem', color:'var(--text-primary)', fontWeight:500, wordBreak:'break-word' }}>{value}</div>
    </div>
  );
}

/* ── Section titre ── */
function SectionTitle({ children }) {
  return (
    <div style={{ fontSize:'0.7rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.09em', color:'var(--text-secondary)', marginBottom:'4px', marginTop:'20px', paddingBottom:'6px', borderBottom:'2px solid var(--border)' }}>
      {children}
    </div>
  );
}

export default function ParticipantDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const { activeChurchId, isPlatformAdmin } = useChurch();
  const churchSettings = useChurchSettings(activeChurchId);
  const { hasGroupAccess } = usePermissions();

  const [participant, setParticipant] = useState(null);
  const [history,     setHistory]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [tab,         setTab]         = useState('info');
  const [showCard,    setShowCard]    = useState(false);
  const [families,    setFamilies]    = useState([]);
  const [familySaving, setFamilySaving] = useState(false);

  useEffect(() => {
    if (!churchSettings.use_families) { setFamilies([]); return; }
    const params = isPlatformAdmin && activeChurchId ? { church_id: activeChurchId } : {};
    api.get('/participants/families/', { params })
      .then(r => setFamilies(Array.isArray(r.data) ? r.data : r.data.results || []))
      .catch(() => setFamilies([]));
  }, [churchSettings.use_families, activeChurchId, isPlatformAdmin]);

  const handleFamilyChange = async (familyId) => {
    setFamilySaving(true);
    try {
      const res = await api.patch(`/participants/${id}/`, { family: familyId || null });
      setParticipant(p => ({ ...p, family: res.data.family, family_name: res.data.family_name }));
      toast.success('Famille mise à jour.');
    } catch {
      toast.error('Erreur lors de la mise à jour de la famille.');
    } finally {
      setFamilySaving(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      api.get(`/participants/${id}/`),
      api.get(`/attendance/?participant=${id}&page_size=200`),
    ]).then(([pRes, aRes]) => {
      setParticipant(pRes.data);
      const raw     = aRes.data?.results ?? (Array.isArray(aRes.data) ? aRes.data : []);
      setHistory(raw.filter(r => !r.is_registration));
    }).catch(err => {
      const s = err.response?.status;
      setError(s === 404 ? 'Participant introuvable.' : s === 403 ? 'Accès refusé.' : `Erreur de chargement (${s || err.message}).`);
    }).finally(() => setLoading(false));
  }, [id, activeChurchId]); // eslint-disable-line

  if (loading) return (
    <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
      <SkeletonCard lines={2} /><SkeletonCard lines={6} />
    </div>
  );

  if (error) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'80px 24px', textAlign:'center' }}>
      <div style={{ width:52, height:52, borderRadius:'50%', background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      </div>
      <div style={{ fontWeight:700, fontSize:'1rem', color:'var(--text-primary)', marginBottom:12 }}>{error}</div>
      <button onClick={() => navigate(-1)}
        style={{ padding:'9px 22px', background:'var(--primary)', color:'#fff', border:'none', borderRadius:9, cursor:'pointer', fontWeight:600, fontSize:'0.875rem' }}>
        Retour
      </button>
    </div>
  );

  if (!participant) return null;

  const sc       = STATUS_COLORS[participant.status] || STATUS_COLORS.membre;
  const initials = `${participant.first_name?.[0]||''}${participant.last_name?.[0]||''}`.toUpperCase();
  const canEdit  = ['admin','superadmin_platform','superviseur','responsable','accueil'].includes(user?.role) || hasGroupAccess('participants');

  // Graphique mensuel
  const monthMap = {};
  history.forEach(r => {
    const d = (r.recorded_at || '').slice(0,7);
    if (d) monthMap[d] = (monthMap[d]||0)+1;
  });
  const chartData = Object.entries(monthMap).sort().slice(-6).map(([k,v]) => ({
    label: new Date(k+'-01').toLocaleDateString('fr-FR',{month:'short', year:'2-digit'}),
    v,
  }));

  const lastPresence = history.length > 0
    ? new Date(history[0].recorded_at).toLocaleDateString('fr-FR', {day:'2-digit', month:'long', year:'numeric'})
    : null;

  const TABS = [
    { key:'info',      label:'Informations' },
    { key:'presences', label:`Présences (${history.length})` },
    { key:'stats',     label:'Statistiques' },
  ];

  return (
    <div style={{ width:'100%' }}>

      {/* ── Breadcrumb ── */}
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'20px', fontSize:'0.84rem' }}>
        <button onClick={() => navigate(-1)}
          style={{ display:'flex', alignItems:'center', gap:'6px', background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, padding:'6px 12px', cursor:'pointer', color:'var(--text-secondary)', fontWeight:500, fontSize:'0.84rem', fontFamily:'inherit' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Retour
        </button>
        <span style={{ color:'var(--text-secondary)' }}>/</span>
        <span style={{ color:'var(--text-secondary)' }}>Participants</span>
        <span style={{ color:'var(--text-secondary)' }}>/</span>
        <span style={{ fontWeight:600, color:'var(--text-primary)' }}>{participant.first_name} {participant.last_name}</span>
      </div>

      {/* ── Carte profil ── */}
      <div style={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:16, padding:'28px', marginBottom:20, boxShadow:'0 1px 6px rgba(0,0,0,0.05)' }}>
        <div style={{ display:'flex', gap:'24px', alignItems:'flex-start', flexWrap:'wrap' }}>

          {/* Avatar */}
          <div style={{ width:90, height:90, borderRadius:'50%', flexShrink:0, overflow:'hidden', background:`${sc.color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.8rem', fontWeight:800, color:sc.color, border:`3px solid ${sc.border}` }}>
            {participant.photo_url
              ? <img src={participant.photo_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              : initials}
          </div>

          {/* Infos principales */}
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginBottom:6 }}>
              <h1 style={{ margin:0, fontSize:'1.5rem', fontWeight:800, color:'var(--text-primary)' }}>
                {participant.first_name} {participant.last_name}
              </h1>
              <span style={{ padding:'3px 12px', borderRadius:99, fontSize:'0.73rem', fontWeight:700, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}` }}>
                {STATUS_LABELS[participant.status] || participant.status}
              </span>
              {participant.gender && (
                <span style={{ padding:'3px 10px', borderRadius:99, fontSize:'0.73rem', fontWeight:600, background:'var(--page-bg)', color:'var(--text-secondary)', border:'1px solid var(--border)' }}>
                  {GENDER_LABELS[participant.gender] || participant.gender}
                </span>
              )}
            </div>

            {/* Détails rapides */}
            <div style={{ display:'flex', gap:'20px', flexWrap:'wrap', marginBottom:14 }}>
              {participant.member_code && (
                <span style={{ fontSize:'0.82rem', color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/></svg>
                  {participant.member_code}
                </span>
              )}
              {participant.phone && (
                <span style={{ fontSize:'0.82rem', color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.28 2 2 0 0 1 3.58 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l1.02-1.02a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  {participant.phone}
                </span>
              )}
              {participant.email && (
                <span style={{ fontSize:'0.82rem', color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  {participant.email}
                </span>
              )}
              {participant.city && (
                <span style={{ fontSize:'0.82rem', color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {participant.city}
                </span>
              )}
            </div>

            {/* KPI rapides */}
            <div style={{ display:'flex', gap:'24px', flexWrap:'wrap' }}>
              <div>
                <div style={{ fontSize:'1.6rem', fontWeight:800, color:'var(--primary)', lineHeight:1 }}>{history.length}</div>
                <div style={{ fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-secondary)', marginTop:2 }}>Présences</div>
              </div>
              {lastPresence && (
                <div>
                  <div style={{ fontSize:'0.9rem', fontWeight:700, color:'var(--text-primary)', lineHeight:1.3 }}>{lastPresence}</div>
                  <div style={{ fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-secondary)', marginTop:2 }}>Dernière présence</div>
                </div>
              )}
            </div>
          </div>

          {/* Boutons action */}
          <div style={{ display:'flex', gap:8, flexShrink:0, flexWrap:'wrap' }}>
            {participant.member_code && (
              <button onClick={() => setShowCard(true)}
                style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 16px', background:'var(--card-bg)', color:'var(--text-primary)', border:'1px solid var(--border)', borderRadius:9, cursor:'pointer', fontWeight:600, fontSize:'0.875rem', fontFamily:'inherit' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                Carte membre
              </button>
            )}
            {canEdit && (
              <button onClick={() => { sessionStorage.setItem('editParticipantId', participant.id); navigate('/participants'); }}
                style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px', background:'var(--primary)', color:'#fff', border:'none', borderRadius:9, cursor:'pointer', fontWeight:600, fontSize:'0.875rem', fontFamily:'inherit' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Modifier
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display:'flex', gap:'2px', background:'var(--page-bg)', border:'1px solid var(--border)', borderRadius:10, padding:'4px', width:'fit-content', marginBottom:20 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding:'7px 20px', border:'none', borderRadius:8, cursor:'pointer', fontWeight: tab===t.key?700:500, fontSize:'0.855rem', fontFamily:'inherit', transition:'all 0.15s',
              background: tab===t.key ? 'var(--primary)' : 'transparent',
              color:       tab===t.key ? '#fff' : 'var(--text-secondary)',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ════ INFORMATIONS ════ */}
      {tab === 'info' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(340px,1fr))', gap:16 }}>

          {/* Colonne 1 — Identité */}
          <div style={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:14, padding:'22px 24px' }}>
            <SectionTitle>Identité</SectionTitle>
            <InfoRow label="Prénom"            value={participant.first_name} />
            <InfoRow label="Nom"               value={participant.last_name} />
            <InfoRow label="Genre"             value={GENDER_LABELS[participant.gender] || participant.gender} />
            <InfoRow label="Statut"            value={STATUS_LABELS[participant.status] || participant.status} />
            <InfoRow label="Église / Centre"   value={participant.church_name || 'Non classé'} />
            {!churchSettings.use_families && participant.family_name && (
              <InfoRow label="Famille"         value={participant.family_name} />
            )}
            {churchSettings.use_families && (
              <div style={{ display:'flex', gap:'12px', padding:'10px 0', borderBottom:'1px solid var(--border)', alignItems:'center' }}>
                <div style={{ width:'170px', flexShrink:0, fontSize:'0.8rem', color:'var(--text-secondary)', fontWeight:600 }}>Famille</div>
                <select
                  value={participant.family || ''}
                  disabled={familySaving}
                  onChange={e => handleFamilyChange(e.target.value)}
                  style={{ fontSize:'0.875rem', fontWeight:500, padding:'5px 8px', borderRadius:6, border:'1px solid var(--border)', background:'var(--card-bg)', color:'var(--text-primary)' }}>
                  <option value="">— Aucune —</option>
                  {families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
            )}
            <InfoRow label="Code membre"       value={participant.member_code} />
            <InfoRow label="Date de naissance" value={participant.birth_date
              ? new Date(participant.birth_date).toLocaleDateString('fr-FR', {day:'2-digit',month:'long',year:'numeric'})
              : participant.birth_year ? `Année ${participant.birth_year}` : null} />
            <InfoRow label="Situation familiale" value={MARITAL_LABELS[participant.marital_status] || participant.marital_status} />
            <InfoRow label="Profession"        value={participant.profession} />
            {participant.notes && (
              <>
                <SectionTitle>Notes</SectionTitle>
                <div style={{ background:'var(--page-bg)', borderRadius:8, padding:'12px 14px', fontSize:'0.875rem', color:'var(--text-primary)', lineHeight:1.65, marginTop:4 }}>
                  {participant.notes}
                </div>
              </>
            )}
          </div>

          {/* Colonne 2 — Contact */}
          <div style={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:14, padding:'22px 24px' }}>
            <SectionTitle>Contact</SectionTitle>
            <InfoRow label="Téléphone"  value={participant.phone} />
            <InfoRow label="WhatsApp"   value={participant.whatsapp} />
            <InfoRow label="Email"      value={participant.email} />
            <InfoRow label="Adresse"    value={participant.address} />
            <InfoRow label="Ville"      value={participant.city} />
            <InfoRow label="Quartier"   value={participant.neighborhood} />

            {(participant.emergency_contact_name || participant.emergency_contact_phone) && (
              <>
                <SectionTitle>Contact d'urgence</SectionTitle>
                <InfoRow label="Nom"       value={participant.emergency_contact_name} />
                <InfoRow label="Téléphone" value={participant.emergency_contact_phone} />
              </>
            )}

            {/* QR code */}
            {(participant.member_code || participant.qr_code) && (
              <>
                <SectionTitle>Code QR</SectionTitle>
                <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:16 }}>
                  <div style={{ padding:8, background:'#fff', borderRadius:10, border:'1px solid var(--border)', display:'inline-flex', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                    <QRCodeSVG
                      value={participant.member_code || String(participant.qr_code)}
                      size={80}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                  <div style={{ fontSize:'0.78rem', color:'var(--text-secondary)', lineHeight:1.8 }}>
                    Code d'identification unique<br/>
                    <strong style={{ color:'var(--text-primary)', fontSize:'0.85rem' }}>{participant.member_code}</strong><br/>
                    <span style={{ fontSize:'0.7rem', opacity:0.6 }}>Scanner pour identifier ce fidèle</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ════ PRESENCES ════ */}
      {tab === 'presences' && (
        <div style={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden' }}>
          {history.length === 0 ? (
            <div style={{ textAlign:'center', padding:'60px 24px', color:'var(--text-secondary)' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity:0.3, marginBottom:12 }}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
              <div style={{ fontWeight:600 }}>Aucune présence enregistrée</div>
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.875rem' }}>
                <thead>
                  <tr style={{ background:'var(--page-bg)', borderBottom:'2px solid var(--border)' }}>
                    <th style={{ padding:'11px 16px', textAlign:'left', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-secondary)' }}>Activité</th>
                    <th style={{ padding:'11px 16px', textAlign:'left', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-secondary)' }}>Date</th>
                    <th style={{ padding:'11px 16px', textAlign:'left', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-secondary)' }}>Enregistré par</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r, i) => (
                    <tr key={r.id} style={{ borderBottom:'1px solid var(--border)', background: i%2===0?'transparent':'var(--page-bg)' }}>
                      <td style={{ padding:'11px 16px', fontWeight:500, color:'var(--text-primary)' }}>
                        <Link to={`/activites/${r.activity}`} style={{ color:'var(--primary)', textDecoration:'none', fontWeight:600 }}>
                          {r.activity_name || `Activité #${r.activity}`}
                        </Link>
                      </td>
                      <td style={{ padding:'11px 16px', color:'var(--text-secondary)', fontSize:'0.84rem' }}>
                        {r.recorded_at ? new Date(r.recorded_at).toLocaleDateString('fr-FR',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                      </td>
                      <td style={{ padding:'11px 16px', color:'var(--text-secondary)', fontSize:'0.84rem' }}>
                        {r.recorded_by_name || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ════ STATISTIQUES ════ */}
      {tab === 'stats' && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* KPI cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:14 }}>
            {[
              { label:'Total présences',   value: history.length,                                  color:'var(--primary)',  bg:'var(--card-bg)' },
              { label:'Mois le plus actif', value: chartData.length ? chartData.reduce((a,b)=>a.v>b.v?a:b).label : '—', color:'#16a34a', bg:'#f0fdf4' },
              { label:'Dernière présence',  value: lastPresence || '—',                             color:'#7c3aed',        bg:'#faf5ff' },
              { label:'Mois avec présences',value: chartData.length,                               color:'#c2410c',        bg:'#fff7ed' },
            ].map(k => (
              <div key={k.label} style={{ background:k.bg, border:'1px solid var(--border)', borderRadius:12, padding:'18px' }}>
                <div style={{ fontSize:'0.68rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.08em', color:k.color, marginBottom:6 }}>{k.label}</div>
                <div style={{ fontSize:'1.5rem', fontWeight:800, color:k.color, lineHeight:1 }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Graphique */}
          {chartData.length > 0 ? (
            <div style={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:14, padding:'22px 24px' }}>
              <div style={{ fontWeight:700, color:'var(--text-primary)', fontSize:'0.9rem', marginBottom:16 }}>Présences par mois (6 derniers mois)</div>
              <BarChart data={chartData} />
            </div>
          ) : (
            <div style={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:14, padding:'40px', textAlign:'center', color:'var(--text-secondary)', fontSize:'0.875rem' }}>
              Pas encore de données de présence.
            </div>
          )}
        </div>
      )}

      {/* ── Modal carte membre ── */}
      {showCard && participant && (
        <MemberCardModal participantId={participant.id} onClose={() => setShowCard(false)} />
      )}

    </div>
  );
}
